import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";
import {
  transformAttribute,
  transformTranslatableTerm,
  transformBrand,
} from "../helpers/productTaxonomyApiHelpers";
import {
  RawWPRestProductCategoriesResponse,
  RawWPRestProductTagsResponse,
  RawWPRestBrand,
  RawWPRestBrandsResponse,
  RawWPRestAttributesResponse,
  RawWPRestAttribute,
  RawMegaMenuResponse,
} from "../../../../types/index";

import type {
  TransformedWPCategory,
  GetWPCategoriesResult,
  TransformedWPTag,
  GetWPTagsResult,
  GetBrandsResult,
  GetAttributesResult,
} from "../types/productTaxonomyApiTypes";

// Get the WordPress REST API base URL from theme variables or use default fallback
const WP_REST_API_BASE =
  window.commonVariables?.wpRestApiBase || "/wp-json/wp/v2";

const CUSTOM_REST_API_BASE =
  window.commonVariables?.customRestApiBase || "/wp-json/ecommerce-app/v1";

// Define the taxonomy API slice by injecting endpoints into the baseApi setup
export const taxonomyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Endpoint to fetch product categories from WordPress REST API
     * Provides a rich dataset including translations and taxonomy data
     * @returns Object containing an array of transformed WP categories
     */
    getCategories: builder.query<GetWPCategoriesResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_cat`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductCategoriesResponse
      ): GetWPCategoriesResult => {
        const languageInfo = selectLanguageInfo(store.getState());

        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getCategories expected an array but received:",
            response
          );
          return { categories: {} }; // Return a valid empty state
        }

        const categoriesById = response.reduce(
          (acc, rawCategory) => {
            // 1. Get translated name/description but keep the raw structure
            const translatedRawCategory = transformTranslatableTerm(
              rawCategory,
              languageInfo
            );

            // 2. Map from RawWPRestProductCategory to TransformedWPCategory
            const transformedCategory: TransformedWPCategory = {
              id: translatedRawCategory.id,
              name: translatedRawCategory.name,
              slug: translatedRawCategory.slug,
              parent: translatedRawCategory.parent,
              count: translatedRawCategory.count,
              description: translatedRawCategory.description,
              link: translatedRawCategory.link,
              taxonomy: translatedRawCategory.taxonomy,
              menuOrder: translatedRawCategory.menu_order,
            };

            acc[rawCategory.id] = transformedCategory;
            return acc;
          },
          {} as Record<number, TransformedWPCategory>
        );

        // Return the categories indexed by ID
        return { categories: categoriesById };
      },
      providesTags: (result) =>
        result?.categories
          ? [
              ...Object.keys(result.categories).map((id) => ({
                type: "Category" as const,
                id,
              })),
              { type: "Category" as const, id: "LIST" },
            ]
          : [{ type: "Category" as const, id: "LIST" }],
    }),

    /**
     * Endpoint to fetch product tags from WordPress REST API
     * Provides a rich dataset including taxonomy data
     * @returns Object containing an array of transformed WP tags
     */
    getTags: builder.query<GetWPTagsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_tag`,
        params: {
          per_page: 100,
        },
      }),
      transformResponse: (
        response: RawWPRestProductTagsResponse
      ): GetWPTagsResult => {
        const languageInfo = selectLanguageInfo(store.getState());

        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getTags expected an array but received:",
            response
          );
          return { tags: {} }; // Return a valid empty state
        }

        const tagsById = response.reduce(
          (acc, tag) => {
            const transformedTag = transformTranslatableTerm(tag, languageInfo);
            acc[tag.id] = transformedTag;
            return acc;
          },
          {} as Record<number, TransformedWPTag>
        );

        // Return the tags indexed by ID
        return { tags: tagsById };
      },
      providesTags: (result) =>
        result?.tags
          ? [
              ...Object.keys(result.tags).map((id) => ({
                type: "Tag" as const,
                id,
              })),
              { type: "Tag" as const, id: "LIST" },
            ]
          : [{ type: "Tag" as const, id: "LIST" }],
    }),

    /**
     * Fetches product brands from the WordPress REST API.
     * Uses /wp/v2/product_brand endpoint.
     */
    getBrands: builder.query<GetBrandsResult, void>({
      query: () => ({
        url: `${WP_REST_API_BASE}/product_brand`,
        params: { per_page: 100, orderby: "name", order: "asc" }, // Fetch up to 100 brands, order by name
      }),
      transformResponse: (
        response: RawWPRestBrandsResponse
      ): GetBrandsResult => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getBrands expected an array but received:",
            response
          );
          return { brands: {} }; // Return a valid empty state
        }
        const rawBrands: RawWPRestBrand[] = response;

        const brandsById = rawBrands.reduce(
          (acc, brand) => {
            acc[brand.id] = transformBrand(brand);
            return acc;
          },
          {} as GetBrandsResult["brands"]
        );

        return { brands: brandsById };
      },
      providesTags: (result) =>
        result?.brands
          ? [
              ...Object.keys(result.brands).map((id) => ({
                type: "Brand" as const,
                id,
              })),
              { type: "Brand" as const, id: "LIST" },
            ]
          : [{ type: "Brand" as const, id: "LIST" }],
    }),

    /**
     * Fetches enriched product attributes from our custom REST endpoint.
     */
    getAttributes: builder.query<GetAttributesResult, void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/product_attributes`,
      }),
      transformResponse: (
        response: RawWPRestAttributesResponse
      ): GetAttributesResult => {
        const languageInfo = selectLanguageInfo(store.getState());

        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getAttributes expected an array but received:",
            response
          );
          return { attributes: {} }; // Return a valid empty state
        }
        const rawAttributes: RawWPRestAttribute[] = response;

        const attributesById = rawAttributes.reduce(
          (acc, attribute) => {
            acc[attribute.id] = transformAttribute(attribute, languageInfo); // Pass dependencies
            return acc;
          },
          {} as GetAttributesResult["attributes"]
        );

        return { attributes: attributesById };
      },
      providesTags: (result) =>
        result?.attributes
          ? [
              ...Object.keys(result.attributes).map((id) => ({
                type: "Attribute" as const,
                id,
              })),
              { type: "Attribute" as const, id: "LIST" },
            ]
          : [{ type: "Attribute" as const, id: "LIST" }],
    }),

    /**
     * Fetches the hierarchical mega menu structure.
     * Uses the custom /ecommerce-app/v1/mega-menu endpoint.
     */
    getMegaMenu: builder.query<RawMegaMenuResponse, void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/mega-menu`,
      }),
      providesTags: ["Category"], // Invalidate if categories change
    }),
  }),
  overrideExisting: false,
});

// Export the auto-generated hooks from the taxonomyApi slice for use in components
export const {
  useGetCategoriesQuery,
  useGetTagsQuery,
  useGetBrandsQuery,
  useGetAttributesQuery,
  useGetMegaMenuQuery,
  util: { getRunningQueriesThunk },
} = taxonomyApi;

// Export the endpoint definitions themselves for potential use in SSR, testing, or manual dispatching
export const { getCategories, getTags, getBrands, getAttributes, getMegaMenu } =
  taxonomyApi.endpoints;
